import '@/lib/amplify';
import * as Sentry from "@sentry/react";
import { Switch, Route, Redirect } from "wouter";
import { queryClient } from "./lib/queryClient";
import { QueryClientProvider } from "@tanstack/react-query";
import { Toaster } from "@/components/ui/toaster";
import { TooltipProvider } from "@/components/ui/tooltip";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import NotFound from "@/pages/not-found";
import FactoryWorld from "@/pages/FactoryWorld";
import AgentBuilder from "@/pages/AgentBuilder";
import MainMenu from "@/pages/MainMenu";
import AgentViewPage from "@/pages/AgentViewPage";
import PreviewPage from "@/pages/PreviewPage";
import ProjectsPage from "@/pages/ProjectsPage";
import BillingPage from "@/pages/BillingPage";
import { ContentDashboard } from "@/features/projects/ContentDashboard";
import { ContentSettings } from "@/features/projects/ContentSettings";
import { KeywordExplorer } from "@/features/projects/KeywordExplorer";
import { Loader2 } from "lucide-react";
import { useEffect } from "react";
import { useKeyboardShortcuts } from "@/hooks/useKeyboardShortcuts";
import { useMenuEvents } from "@/hooks/useMenuEvents";
import { useWorkerEvents } from "@/hooks/useWorkerEvents";
import { useAiChatEvents } from "@/hooks/useAiChatEvents";
import { useWorkspaceFeatures } from "@/hooks/useWorkspaceFeatures";
import { useOnboardingAutoAdvance } from "@/hooks/useOnboardingAutoAdvance";
import { useWorldStore, flushPendingWorldSaves } from "@/stores/worldStore";
import { useLocalTerminalStore } from "@/stores/localTerminalStore";
import { useUIStore } from "@/stores/uiStore";
import { UpgradePrompt } from "@/features/billing/UpgradePrompt";
import { CommandPalette } from "@/components/CommandPalette";
import { AppLayout } from "@/components/AppLayout";

function LoadingScreen() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-colony-bg">
      <div className="flex flex-col items-center gap-4">
        <Loader2 className="w-8 h-8 animate-spin text-colony-brand" />
        <p className="text-colony-text-tertiary">Loading...</p>
      </div>
    </div>
  );
}

function GuardedPreview() {
  const { canPreview } = useWorkspaceFeatures();
  if (!canPreview) return <Redirect to="/" />;
  return <PreviewPage />;
}

function ProtectedRoutes() {
  const { isAuthenticated, isLoading, user } = useAuth();
  const planLimitError = useWorldStore((s) => s.planLimitError);
  const clearPlanLimitError = useWorldStore((s) => s.clearPlanLimitError);
  const commandPaletteOpen = useUIStore((s) => s.commandPaletteOpen);
  const setCommandPaletteOpen = useUIStore((s) => s.setCommandPaletteOpen);
  useKeyboardShortcuts();
  useMenuEvents();
  useWorkerEvents();
  useAiChatEvents();
  useOnboardingAutoAdvance();

  useEffect(() => {
    if (isAuthenticated && user) {
      Sentry.setUser({ id: user.userId, username: user.username, email: user.email });
    } else {
      Sentry.setUser(null);
    }
  }, [isAuthenticated, user]);

  // Drive terminal activity status (active → idle → disconnected) so the
  // "My Agents" view can tell which terminals are still working. Without this
  // sweep every connected terminal is reported as "running" forever.
  useEffect(() => {
    const { startIdleDetection, stopIdleDetection } = useLocalTerminalStore.getState();
    startIdleDetection();
    return () => stopIdleDetection();
  }, []);

  // Persist any pending debounced World saves when the window is hidden or
  // closing. The app is still alive at this point, so the async writes can
  // finish — otherwise a change made within the debounce window is lost.
  useEffect(() => {
    const flush = () => { void flushPendingWorldSaves(); };
    const onVisibility = () => { if (document.visibilityState === "hidden") flush(); };
    document.addEventListener("visibilitychange", onVisibility);
    window.addEventListener("pagehide", flush);
    return () => {
      document.removeEventListener("visibilitychange", onVisibility);
      window.removeEventListener("pagehide", flush);
    };
  }, []);

  if (isLoading) {
    return <LoadingScreen />;
  }

  if (!isAuthenticated) {
    return <MainMenu />;
  }

  return (
    <>
      <AppLayout>
        <Switch>
          <Route path="/" component={AgentViewPage} />
          <Route path="/world" component={FactoryWorld} />
          <Route path="/preview" component={GuardedPreview} />
          <Route path="/projects" component={ProjectsPage} />
          <Route path="/projects/:id/content/settings" component={ContentSettings} />
          <Route path="/projects/:id/content/keywords" component={KeywordExplorer} />
          <Route path="/projects/:id/content" component={ContentDashboard} />
          <Route path="/projects/:id" component={ProjectsPage} />
          <Route path="/agent-builder" component={AgentBuilder} />
          <Route path="/billing" component={BillingPage} />
          <Route component={NotFound} />
        </Switch>
      </AppLayout>
      {commandPaletteOpen && (
        <CommandPalette onClose={() => setCommandPaletteOpen(false)} />
      )}
      <UpgradePrompt
        open={!!planLimitError}
        onOpenChange={(open) => { if (!open) clearPlanLimitError(); }}
        limitCode={planLimitError?.code ?? "PLAN_LIMIT_PROJECTS"}
        limit={planLimitError?.limit}
        current={planLimitError?.current}
      />
    </>
  );
}

function App() {
  return (
    <Sentry.ErrorBoundary fallback={<p>Something went wrong</p>}>
      <QueryClientProvider client={queryClient}>
        <TooltipProvider>
          <AuthProvider>
            <ProtectedRoutes />
            <Toaster />
          </AuthProvider>
        </TooltipProvider>
      </QueryClientProvider>
    </Sentry.ErrorBoundary>
  );
}

export default App;
